import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Grid,
  Input,
  List,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilterOutlined,
  LoginOutlined,
  LogoutOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useAuth } from "../context/AuthContext";
import useAuditScope from "./useAuditScope";
import {
  createAuditRequestGate,
  isAuditRequestCanceled,
} from "./auditRequestGate";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { useBreakpoint } = Grid;

const ACTION_CONFIG = {
  LOGIN: {
    label: "Connexion",
    color: "green",
    icon: <LoginOutlined />,
  },
  LOGOUT: {
    label: "Déconnexion",
    color: "blue",
    icon: <LogoutOutlined />,
  },
  LOGIN_FAILURE: {
    label: "Echec connexion",
    color: "red",
    icon: <CloseCircleOutlined />,
  },
};

const STATUS_CONFIG = {
  success: { color: "green", icon: <CheckCircleOutlined />, label: "Succès" },
  failure: { color: "red", icon: <CloseCircleOutlined />, label: "Echec" },
  partial: { color: "orange", icon: null, label: "Partiel" },
};

const FAILURE_REASON_LABELS = {
  MISSING_CREDENTIALS: "Identifiants manquants",
  USER_NOT_FOUND: "Utilisateur introuvable",
  WRONG_PASSWORD: "Mot de passe incorrect",
  LOGIN_DISABLED: "Connexion désactivée",
  USER_INACTIVE: "Compte inactif",
  COMPANY_DISABLED: "Entreprise désactivée",
  TRIAL_EXPIRED: "Période d'essai expirée",
  USER_INITIATED: "Déconnexion volontaire",
};

const safeParseDetails = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const AuditConnexions = () => {
  const { token } = useAuth();
  const {
    auditScope,
    companyId,
    isSuperAdmin,
    scope: effectiveAuditScope,
    scopeKey: auditScopeKey,
    setAuditScope,
  } = useAuditScope();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const latestAuditScopeKeyRef = useRef(auditScopeKey);
  useLayoutEffect(() => {
    latestAuditScopeKeyRef.current = auditScopeKey;
  }, [auditScopeKey]);
  const requestGateRef = useRef(null);
  if (!requestGateRef.current) {
    requestGateRef.current = createAuditRequestGate();
  }

  const [filters, setFilters] = useState({
    actionType: null,
    userSearch: "",
    ipAddress: "",
    dateRange: null,
  });

  const loadEvents = useCallback(
    async (page, size, currentFilters) => {
      const request = requestGateRef.current.start(auditScopeKey);
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append("limit", String(size));
        params.append("offset", String((page - 1) * size));
        if (effectiveAuditScope === "all") {
          params.append("scope", "all");
        }

        if (currentFilters.actionType) {
          params.append("actionType", currentFilters.actionType);
        }
        if (currentFilters.userSearch) {
          params.append("userSearch", currentFilters.userSearch);
        }
        if (currentFilters.ipAddress) {
          params.append("ipAddress", currentFilters.ipAddress);
        }
        if (currentFilters.dateRange?.[0] && currentFilters.dateRange?.[1]) {
          params.append(
            "startDate",
            currentFilters.dateRange[0].startOf("day").format("YYYY-MM-DD HH:mm:ss")
          );
          params.append(
            "endDate",
            currentFilters.dateRange[1].endOf("day").format("YYYY-MM-DD HH:mm:ss")
          );
        }

        const response = await axios.get(
          `/api/audit/auth/history?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(companyId ? { "X-Company-Id": String(companyId) } : {}),
            },
            signal: request.signal,
          }
        );

        if (
          request.isCurrent(latestAuditScopeKeyRef.current) &&
          response.data?.success
        ) {
          setEvents(response.data.events || []);
          setTotal(response.data.total || 0);
        }
      } catch (error) {
        if (
          isAuditRequestCanceled(error, request.signal) ||
          !request.isCurrent(latestAuditScopeKeyRef.current)
        ) {
          return;
        }
        console.error("[AUDIT_CONNEXIONS] Erreur chargement:", error);
        message.error("Impossible de charger l'historique des connexions");
      } finally {
        const isCurrent = request.isCurrent(latestAuditScopeKeyRef.current);
        request.finish();
        if (isCurrent) setLoading(false);
      }
    },
    [auditScopeKey, companyId, effectiveAuditScope, token]
  );

  useEffect(() => {
    requestGateRef.current.cancel();
    setEvents([]);
    setTotal(0);
    setCurrentPage(1);
    loadEvents(1, pageSize, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditScopeKey, loadEvents]);

  useEffect(
    () => () => {
      requestGateRef.current?.cancel();
    },
    []
  );

  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadEvents(1, pageSize, filters);
  };

  const handleResetFilters = () => {
    const cleared = {
      actionType: null,
      userSearch: "",
      ipAddress: "",
      dateRange: null,
    };
    setFilters(cleared);
    setCurrentPage(1);
    loadEvents(1, pageSize, cleared);
  };

  const renderActionTag = (action) => {
    const cfg = ACTION_CONFIG[action] || {
      label: action || "-",
      color: "default",
      icon: null,
    };
    return (
      <Tag icon={cfg.icon} color={cfg.color}>
        {cfg.label}
      </Tag>
    );
  };

  const renderStatusTag = (status) => {
    const cfg = STATUS_CONFIG[status] || {
      color: "default",
      icon: null,
      label: status || "-",
    };
    return (
      <Tag icon={cfg.icon} color={cfg.color}>
        {cfg.label}
      </Tag>
    );
  };

  const columns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "created_at",
        key: "created_at",
        width: 170,
        render: (value) =>
          value ? (
            <Tooltip title={dayjs(value).format("DD/MM/YYYY HH:mm:ss")}>
              <Text>{dayjs(value).format("DD/MM/YYYY HH:mm")}</Text>
            </Tooltip>
          ) : (
            "-"
          ),
      },
      {
        title: "Action",
        dataIndex: "action_type",
        key: "action_type",
        width: 140,
        render: renderActionTag,
      },
      {
        title: "Utilisateur",
        key: "user",
        render: (_, record) => {
          const details = safeParseDetails(record.details);
          const displayName = record.username || details.email || record.user_email || "Inconnu";
          return (
            <Space direction="vertical" size={0}>
              <Text strong>{displayName}</Text>
              {record.user_email && record.user_email !== displayName ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.user_email}
                </Text>
              ) : null}
            </Space>
          );
        },
      },
      {
        title: "Entreprise",
        dataIndex: "company_name",
        key: "company_name",
        responsive: ["md"],
        render: (value) => value || <Text type="secondary">-</Text>,
      },
      {
        title: "Statut",
        dataIndex: "status",
        key: "status",
        width: 110,
        render: renderStatusTag,
      },
      {
        title: "Motif",
        key: "reason",
        responsive: ["lg"],
        render: (_, record) => {
          const details = safeParseDetails(record.details);
          if (!details.reason) return <Text type="secondary">-</Text>;
          return (
            <Text>{FAILURE_REASON_LABELS[details.reason] || details.reason}</Text>
          );
        },
      },
      {
        title: "IP",
        dataIndex: "ip_address",
        key: "ip_address",
        width: 140,
        responsive: ["md"],
        render: (value) =>
          value ? (
            <Text code style={{ fontSize: 12 }}>
              {value}
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
    ],
    []
  );

  const renderMobileItem = (record) => {
    const details = safeParseDetails(record.details);
    const displayName = record.username || details.email || record.user_email || "Inconnu";
    return (
      <List.Item key={record.id}>
        <Card size="small" style={{ width: "100%" }}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Space wrap>
              {renderActionTag(record.action_type)}
              {renderStatusTag(record.status)}
            </Space>
            <Text strong>{displayName}</Text>
            {record.company_name ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.company_name}
              </Text>
            ) : null}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss")}
            </Text>
            {details.reason ? (
              <Text style={{ fontSize: 12 }}>
                Motif :{" "}
                {FAILURE_REASON_LABELS[details.reason] || details.reason}
              </Text>
            ) : null}
            {record.ip_address ? (
              <Text code style={{ fontSize: 11 }}>
                {record.ip_address}
              </Text>
            ) : null}
          </Space>
        </Card>
      </List.Item>
    );
  };

  return (
    <div className="audit-connexions" style={{ padding: isMobile ? 8 : 16 }}>
      <Card
        title={
          <Space>
            <LoginOutlined />
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
              Historique des connexions
            </Title>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadEvents(currentPage, pageSize, filters)}
            loading={loading}
            size={isMobile ? "small" : "middle"}
          >
            {isMobile ? "" : "Actualiser"}
          </Button>
        }
        bordered={false}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Les évènements LOGIN, LOGOUT et tentatives échouées sont écrits via le buffer audit (impact CPU négligeable)."
        />

        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Space wrap size="middle">
            {isSuperAdmin ? (
              <Select
                value={auditScope}
                onChange={(value) => setAuditScope(value || "company")}
                style={{ width: 210 }}
              >
                <Option value="company">Entreprise sélectionnée</Option>
                <Option value="all">Toutes les entreprises</Option>
              </Select>
            ) : null}
            <Select
              placeholder="Type d'évènement"
              value={filters.actionType}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, actionType: value }))
              }
              allowClear
              style={{ width: 200 }}
            >
              {Object.entries(ACTION_CONFIG).map(([value, cfg]) => (
                <Option key={value} value={value}>
                  {cfg.label}
                </Option>
              ))}
            </Select>

            <Input
              placeholder="Email ou nom"
              value={filters.userSearch}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, userSearch: e.target.value }))
              }
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 220 }}
              onPressEnter={handleApplyFilters}
            />

            <Input
              placeholder="Adresse IP"
              value={filters.ipAddress}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, ipAddress: e.target.value }))
              }
              allowClear
              style={{ width: 180 }}
              onPressEnter={handleApplyFilters}
            />

            <RangePicker
              value={filters.dateRange}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, dateRange: value }))
              }
              format="DD/MM/YYYY"
            />

            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={handleApplyFilters}
            >
              Filtrer
            </Button>
            <Button onClick={handleResetFilters}>Reinitialiser</Button>
          </Space>

          {isMobile ? (
            <List
              loading={loading}
              dataSource={events}
              renderItem={renderMobileItem}
              locale={{ emptyText: "Aucun évènement" }}
            />
          ) : (
            <Table
              loading={loading}
              dataSource={events}
              columns={columns}
              rowKey="id"
              pagination={false}
              size="middle"
              locale={{ emptyText: "Aucun évènement" }}
            />
          )}

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              pageSizeOptions={["20", "50", "100", "200"]}
              showTotal={(t) => `${t} évènement(s)`}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
                loadEvents(page, size, filters);
              }}
              size={isMobile ? "small" : "default"}
            />
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default AuditConnexions;
